import { AxiosError } from "axios";
import CryptoJS from "crypto-js";
import { CustomAxiosType } from "../types/types";
import { client } from "./client";

/** tb_shop_zone 한 행 (백엔드 ShopZoneDto) */
export interface ShopZoneRow {
  shop_zone_id: number;
  ogdp_id: string;
  ogdp_nm: string | null;
  zone_nm: string;
  lat: number;
  lot: number;
  radius_m: number;
  apply_ocrn_type_cd: string;
  zone_rmrk: string | null;
  sd_nm: string | null;
  sgg_nm: string | null;
  /** yyyy-MM-dd HH:mm:ss. 수정할 때 그대로 돌려보내 동시 수정을 확인한다 */
  mdfcn_dt: string;
}

export interface ShopZonePatch {
  shop_zone_id: number;
  lat: number;
  lot: number;
  radius_m: number;
  mdfcn_dt?: string;
}

// 안전구역 목록 (인플랩 관리자만)
export const getShopZones = async () => {
  const response = await client.post<CustomAxiosType<ShopZoneRow[] | null>>(
    `/api/shopZone/list/get`,
    {}
  );
  return response.data.result ?? [];
};

// 안전구역 위치·반경 수정. 성공하면 저장된 행을 돌려준다
export const patchShopZone = async (patch: ShopZonePatch) => {
  const response = await client.post<CustomAxiosType<ShopZoneRow>>(
    `/api/shopZone/patch`,
    patch
  );
  return response.data.result;
};

/** 안전구역 안이라서 보내지 않은 알람 한 건 (백엔드 ShopZoneSkipDto) */
export interface ShopZoneSkipRow {
  skip_id: number;
  /** 억제 일시 yyyy-MM-dd HH:mm:ss */
  skip_dt: string;
  /** 이벤트 발생 일시 (tb_ocrn_log) */
  ocrn_dt: string | null;
  ocrn_id: number | null;
  /** 2 전복사고, 3 위험운전 */
  ocrn_type_cd: string;
  /** 0 신규(미처리), 1 취소, 2 해제·확인 */
  prcs_stts_cd: number | null;
  rmv_rsn: string | null;
  dvc_sn: string | null;
  vhcl_id: number | null;
  vhcl_nm: string | null;
  ogdp_id: string | null;
  ogdp_nm: string | null;
  ocrn_lat: number | string | null;
  ocrn_lot: number | string | null;
  shop_zone_id: number | null;
  zone_nm: string | null;
  zone_lat: number | string | null;
  zone_lot: number | string | null;
  radius_m: number | null;
  /** 구역 중심까지 거리(m) */
  dist_m: number | null;
}

export interface ShopZoneSkipFilter {
  startDate: string;
  endDate: string;
  shopZoneId: number | null;
  /** "2" 전복, "3" 위험운전, null 전체 */
  ocrnTypeCd: string | null;
  page: number;
  limit: number;
}

export interface ShopZoneSkipResult {
  startDate: string;
  endDate: string;
  page: number;
  limit: number;
  /** 집계값이 문자열(DECIMAL)로 올 수 있어 Number() 로 읽는다 */
  summary: {
    total: number | string;
    rollover: number | string;
    danger: number | string;
    devices: number | string;
    zones: number | string;
  };
  items: ShopZoneSkipRow[];
  /** 조건에 맞는 전체 건수 (응답 count) */
  total: number;
}

// 안전구역 억제 알람 목록 (인플랩 관리자만)
export const getShopZoneSkips = async (
  filter: ShopZoneSkipFilter
): Promise<ShopZoneSkipResult> => {
  const response = await client.post<
    CustomAxiosType<Omit<ShopZoneSkipResult, "total"> | null>
  >(`/api/shopZone/skip/get`, filter);
  const r = response.data.result;
  return {
    startDate: r?.startDate ?? filter.startDate,
    endDate: r?.endDate ?? filter.endDate,
    page: r?.page ?? filter.page,
    limit: r?.limit ?? filter.limit,
    summary: r?.summary ?? { total: 0, rollover: 0, danger: 0, devices: 0, zones: 0 },
    items: r?.items ?? [],
    total: response.data.count ?? 0,
  };
};

export const SHOP_ZONE_CONFLICT = 4002;
export const SHOP_ZONE_PERMISSION = 4008;

/**
 * 실패 응답을 풀어 준다. 400 응답은 client 인터셉터가 result 를 풀지 않으므로 여기서 푼다.
 * 동시 수정 충돌(4002)이면 result 에 DB 의 지금 값이 들어 있다.
 */
export const readShopZoneError = (
  err: unknown
): { code?: number; message: string; current?: ShopZoneRow } => {
  const e = err as AxiosError<{ code?: number; message?: string; result?: unknown }>;
  const data = e?.response?.data;
  let current: ShopZoneRow | undefined;
  if (data && typeof data.result === "string") {
    try {
      current = JSON.parse(
        CryptoJS.enc.Base64.parse(data.result).toString(CryptoJS.enc.Utf8)
      );
    } catch {
      current = undefined;
    }
  }
  const status = e?.response?.status;
  return {
    code: data?.code,
    message:
      data?.message ||
      (status ? `HTTP ${status}` : e?.message || "알 수 없는 오류"),
    current,
  };
};
